import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Tag, Toast } from 'react-vant';
import {
  createDivination,
  createOrder,
  fetchHistory,
  fetchMe,
  fetchPlans,
  fetchSubscription,
  getStoredToken,
  login,
  logout,
  mockPay,
  register,
  updateProfile
} from './api.js';

const METHOD_OPTIONS = [
  { code: 'time', label: '时间起卦', hint: '按当前或指定时辰，以农历年月日时起卦' },
  { code: 'number', label: '数字起卦', hint: '输入两个以上数字，如 8, 6, 12' },
  { code: 'text', label: '文字起卦', hint: '输入心中所念的一句话' }
];

const NAV_ITEMS = [
  { code: 'home', label: '首页', mark: '卦' },
  { code: 'history', label: '记录', mark: '记' },
  { code: 'account', label: '我的', mark: '我' },
  { code: 'settings', label: '设置', mark: '设' }
];

const GENDER_LABELS = { male: '男', female: '女', other: '其他' };
const HOURS = Array.from({ length: 24 }, (_, index) => String(index).padStart(2, '0'));
const MINUTES_SECONDS = Array.from({ length: 60 }, (_, index) => String(index).padStart(2, '0'));

function emptyProfile() {
  return {
    phone: '',
    password: '',
    nickname: '',
    gender: '',
    birthDate: '',
    birthHour: '',
    birthMinute: '',
    birthSecond: '',
    address: ''
  };
}

function getDeviceId() {
  const key = 'yishu_huisuan_device_id';
  const existing = localStorage.getItem(key);
  if (existing) return existing;
  const value = `web_${createClientId()}`;
  localStorage.setItem(key, value);
  return value;
}

function createClientId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  if (globalThis.crypto?.getRandomValues) {
    const bytes = new Uint8Array(16);
    globalThis.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));
    return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`;
  }
  return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

function formatMoney(value) {
  return Number(value).toLocaleString('zh-CN', { minimumFractionDigits: value % 1 ? 1 : 0 });
}

function parseAlgorithmResult(value) {
  if (!value || typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function splitBirthTime(value) {
  if (!value) return { birthHour: '', birthMinute: '', birthSecond: '' };
  const [birthHour = '', birthMinute = '', birthSecond = ''] = String(value).split(':');
  return { birthHour, birthMinute, birthSecond };
}

function buildBirthTime(form) {
  const parts = [form.birthHour, form.birthMinute, form.birthSecond];
  if (parts.every((item) => !item)) return null;
  if (parts.some((item) => !item)) {
    throw new Error('出生时分秒如果填写，需要时、分、秒都选完整');
  }
  return parts.join(':');
}

function profilePayload(form) {
  return {
    nickname: form.nickname.trim(),
    gender: form.gender,
    birthDate: form.birthDate,
    birthTime: buildBirthTime(form),
    address: form.address.trim()
  };
}

function formFromUser(user) {
  return {
    ...emptyProfile(),
    nickname: user?.nickname || '',
    gender: user?.gender || '',
    birthDate: user?.birthDate || '',
    address: user?.address || '',
    ...splitBirthTime(user?.birthTime)
  };
}

export default function App() {
  const [deviceId] = useState(getDeviceId);
  const [activeTab, setActiveTab] = useState('home');
  const [token, setToken] = useState(getStoredToken);
  const [user, setUser] = useState(null);
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState({ active: false });
  const [method, setMethod] = useState('time');
  const [plan, setPlan] = useState('single');
  const [question, setQuestion] = useState('');
  const [numbers, setNumbers] = useState('8, 6, 12');
  const [text, setText] = useState('事业何时有突破');
  const [datetime, setDatetime] = useState(() => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16));
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [authMode, setAuthMode] = useState('login');
  const [loginForm, setLoginForm] = useState({ phone: '', password: '' });
  const [registerForm, setRegisterForm] = useState(emptyProfile);
  const [profileForm, setProfileForm] = useState(emptyProfile);
  const [settings, setSettings] = useState(() => ({
    notify: localStorage.getItem('yishu_notify') !== 'off',
    saveDraft: localStorage.getItem('yishu_save_draft') !== 'off'
  }));

  const selectedMethod = useMemo(() => METHOD_OPTIONS.find((item) => item.code === method), [method]);
  const selectedPlan = useMemo(() => plans.find((item) => item.code === plan), [plans, plan]);

  const refreshMeta = useCallback(async () => {
    if (token === undefined) return;
    const [planList, subStatus, records] = await Promise.all([
      fetchPlans(),
      fetchSubscription(deviceId),
      fetchHistory(deviceId)
    ]);
    setPlans(planList);
    setSubscription(subStatus);
    setHistory(records);
  }, [deviceId, token]);

  useEffect(() => {
    refreshMeta().catch(() => Toast.fail('初始化失败，请确认后端已启动'));
  }, [refreshMeta]);

  useEffect(() => {
    let alive = true;
    if (!token) {
      setUser(null);
      setProfileForm(emptyProfile());
      return undefined;
    }
    fetchMe()
      .then((nextUser) => {
        if (!alive) return;
        setUser(nextUser);
        setProfileForm(formFromUser(nextUser));
      })
      .catch(() => {
        if (!alive) return;
        setToken('');
        setUser(null);
      });
    return () => {
      alive = false;
    };
  }, [token]);

  const payload = useMemo(() => {
    if (method === 'time') return { datetime };
    if (method === 'number') return { numbers };
    return { text };
  }, [datetime, method, numbers, text]);

  async function handleSubmit() {
    setLoading(true);
    setResult(null);
    try {
      Toast.loading({ message: '正在起卦...', duration: 0, forbidClick: true });
      const order = subscription.active && plan === 'monthly' ? null : await createOrder(deviceId, plan);
      if (order?.mockEnabled) await mockPay(order.id);
      const data = await createDivination({
        deviceId,
        method,
        question,
        payload,
        plan,
        orderId: order?.id
      });
      setResult(data);
      await refreshMeta();
      Toast.clear();
      Toast.success('解读完成');
    } catch (error) {
      Toast.clear();
      Toast.fail(error.response?.data?.message || error.message || '起卦失败');
    } finally {
      setLoading(false);
    }
  }

  async function handleLogin(event) {
    event.preventDefault();
    try {
      const data = await login(loginForm);
      setToken(data.token);
      setUser(data.user);
      setProfileForm(formFromUser(data.user));
      await refreshMeta();
      Toast.success('登录成功');
      setActiveTab('account');
    } catch (error) {
      Toast.fail(error.response?.data?.message || '登录失败');
    }
  }

  async function handleRegister(event) {
    event.preventDefault();
    try {
      const data = await register({ ...profilePayload(registerForm), phone: registerForm.phone, password: registerForm.password });
      setToken(data.token);
      setUser(data.user);
      setProfileForm(formFromUser(data.user));
      setRegisterForm(emptyProfile());
      await refreshMeta();
      Toast.success('注册成功');
      setActiveTab('account');
    } catch (error) {
      Toast.fail(error.response?.data?.issues?.[0]?.message || error.response?.data?.message || error.message || '注册失败');
    }
  }

  async function handleProfileSave(event) {
    event.preventDefault();
    try {
      const nextUser = await updateProfile(profilePayload(profileForm));
      setUser(nextUser);
      setProfileForm(formFromUser(nextUser));
      Toast.success('资料已保存');
    } catch (error) {
      Toast.fail(error.response?.data?.issues?.[0]?.message || error.response?.data?.message || error.message || '保存失败');
    }
  }

  async function handleLogout() {
    await logout();
    setToken('');
    setUser(null);
    setActiveTab('home');
    Toast.success('已退出登录');
  }

  function updateSetting(key, value) {
    const next = { ...settings, [key]: value };
    setSettings(next);
    localStorage.setItem(key === 'notify' ? 'yishu_notify' : 'yishu_save_draft', value ? 'on' : 'off');
  }

  return (
    <main className="app-shell">
      <TopBar user={user} onAccount={() => setActiveTab('account')} />
      {activeTab === 'home' && (
        <HomePage
          method={method}
          setMethod={setMethod}
          plan={plan}
          setPlan={setPlan}
          question={question}
          setQuestion={setQuestion}
          numbers={numbers}
          setNumbers={setNumbers}
          text={text}
          setText={setText}
          datetime={datetime}
          setDatetime={setDatetime}
          selectedMethod={selectedMethod}
          selectedPlan={selectedPlan}
          plans={plans}
          subscription={subscription}
          loading={loading}
          result={result}
          onSubmit={handleSubmit}
        />
      )}
      {activeTab === 'history' && <HistoryPage history={history} />}
      {activeTab === 'account' && (
        <AccountPage
          user={user}
          authMode={authMode}
          setAuthMode={setAuthMode}
          loginForm={loginForm}
          setLoginForm={setLoginForm}
          registerForm={registerForm}
          setRegisterForm={setRegisterForm}
          profileForm={profileForm}
          setProfileForm={setProfileForm}
          onLogin={handleLogin}
          onRegister={handleRegister}
          onProfileSave={handleProfileSave}
          onLogout={handleLogout}
        />
      )}
      {activeTab === 'settings' && <SettingsPage deviceId={deviceId} settings={settings} updateSetting={updateSetting} user={user} />}
      <BottomNav activeTab={activeTab} setActiveTab={setActiveTab} />
    </main>
  );
}

function TopBar({ user, onAccount }) {
  return (
    <header className="top-bar">
      <div>
        <strong>易数慧算</strong>
        <small>{user ? `${user.nickname}，今日宜稳问细断` : '登录后保存生辰与历史记录'}</small>
      </div>
      <button type="button" className="account-chip" onClick={onAccount}>{user ? '我的账户' : '登录/注册'}</button>
    </header>
  );
}

function HomePage(props) {
  return (
    <>
      <section className="hero-card">
        <div className="hero-orbit" />
        <p className="eyebrow">Meihua AI Divination</p>
        <h1>易数慧算</h1>
        <p className="hero-copy">以梅花易数排卦，用 DeepSeek 生成克制、清晰、能落地的卦象解读。</p>
        <div className="hero-tags">
          <Tag type="warning">账户记录</Tag>
          <Tag type="primary">三种起卦</Tag>
          <Tag type="success">按次付费</Tag>
        </div>
      </section>

      <section className="panel">
        <div className="section-title"><span>01</span><h2>选择起卦方式</h2></div>
        <div className="segmented-list">
          {METHOD_OPTIONS.map((item) => (
            <button className={`choice ${props.method === item.code ? 'active' : ''}`} key={item.code} onClick={() => props.setMethod(item.code)} type="button">
              <strong>{item.label}</strong><small>{item.hint}</small>
            </button>
          ))}
        </div>
      </section>

      <section className="panel form-panel">
        <div className="section-title"><span>02</span><h2>填写问题</h2></div>
        <label className="field-label">你想问什么
          <textarea value={props.question} onChange={(event) => props.setQuestion(event.target.value)} placeholder="例如：这次转岗机会是否值得争取？" rows={3} />
        </label>
        {props.method === 'time' && <label className="field-label">指定时间<input type="datetime-local" value={props.datetime} onChange={(event) => props.setDatetime(event.target.value)} /></label>}
        {props.method === 'number' && <label className="field-label">数字<input value={props.numbers} onChange={(event) => props.setNumbers(event.target.value)} placeholder="8, 6, 12" /></label>}
        {props.method === 'text' && <label className="field-label">文字<input value={props.text} onChange={(event) => props.setText(event.target.value)} placeholder="输入心中突然出现的一句话" /></label>}
        <p className="method-note">当前：{props.selectedMethod?.hint}</p>
      </section>

      <section className="panel">
        <div className="section-title"><span>03</span><h2>选择套餐</h2></div>
        <div className="plans">
          {props.plans.map((item) => (
            <button className={`plan-card ${props.plan === item.code ? 'active' : ''}`} key={item.code} onClick={() => props.setPlan(item.code)} type="button">
              <span>{item.name}</span><strong>¥{formatMoney(item.amount)}</strong><small>{item.description}</small>
            </button>
          ))}
        </div>
        {props.subscription.active && <p className="subscription-tip">包月会员生效中，选择包月可直接解读。</p>}
        <Button block type="primary" round loading={props.loading} onClick={props.onSubmit} className="submit-button">
          {props.selectedPlan ? `支付 ¥${formatMoney(props.selectedPlan.amount)} 并开始解读` : '开始解读'}
        </Button>
      </section>
      {props.result && <ResultView data={props.result} />}
    </>
  );
}

function HistoryPage({ history }) {
  return (
    <section className="panel history-panel page-panel">
      <div className="section-title"><span>记</span><h2>最近记录</h2></div>
      {history.length === 0 ? <p className="empty">还没有记录，第一卦总是最像开门声。</p> : history.map((item) => {
        const algorithmResult = parseAlgorithmResult(item.algorithm_result);
        return (
          <article className="history-item" key={item.id}>
            <div><strong>{algorithmResult?.main?.name || '卦象'}</strong><small>{item.question || '未填写问题'}</small></div>
            <Tag plain>{item.plan_code}</Tag>
          </article>
        );
      })}
    </section>
  );
}

function AccountPage(props) {
  if (!props.user) {
    return (
      <section className="panel page-panel">
        <div className="section-title"><span>我</span><h2>账户中心</h2></div>
        <div className="auth-switch">
          <button type="button" className={props.authMode === 'login' ? 'active' : ''} onClick={() => props.setAuthMode('login')}>登录</button>
          <button type="button" className={props.authMode === 'register' ? 'active' : ''} onClick={() => props.setAuthMode('register')}>注册</button>
        </div>
        {props.authMode === 'login' ? (
          <form className="stack-form" onSubmit={props.onLogin}>
            <TextField label="手机号" value={props.loginForm.phone} onChange={(value) => props.setLoginForm({ ...props.loginForm, phone: value })} placeholder="请输入手机号" />
            <TextField label="密码" type="password" value={props.loginForm.password} onChange={(value) => props.setLoginForm({ ...props.loginForm, password: value })} placeholder="至少 6 位" />
            <Button block round type="primary" nativeType="submit" className="submit-button">登录</Button>
          </form>
        ) : (
          <ProfileForm form={props.registerForm} setForm={props.setRegisterForm} onSubmit={props.onRegister} includeAuth submitText="注册并保存生辰" />
        )}
      </section>
    );
  }

  return (
    <>
      <section className="account-card">
        <p className="eyebrow">Account</p>
        <h2>{props.user.nickname}</h2>
        <div className="profile-pills"><span>{GENDER_LABELS[props.user.gender]}</span><span>{props.user.birthDate}</span><span>{props.user.birthTime || '未填时分秒'}</span></div>
        <p>{props.user.address}</p>
      </section>
      <section className="panel page-panel">
        <div className="section-title"><span>档</span><h2>基础资料</h2></div>
        <ProfileForm form={props.profileForm} setForm={props.setProfileForm} onSubmit={props.onProfileSave} submitText="保存资料" />
        <Button block round type="default" onClick={props.onLogout} className="ghost-button">退出登录</Button>
      </section>
    </>
  );
}

function ProfileForm({ form, setForm, onSubmit, includeAuth = false, submitText }) {
  return (
    <form className="stack-form" onSubmit={onSubmit}>
      {includeAuth && <TextField label="手机号 *" value={form.phone} onChange={(value) => setForm({ ...form, phone: value })} placeholder="11 位手机号" />}
      {includeAuth && <TextField label="密码 *" type="password" value={form.password} onChange={(value) => setForm({ ...form, password: value })} placeholder="至少 6 位" />}
      <TextField label="昵称 *" value={form.nickname} onChange={(value) => setForm({ ...form, nickname: value })} placeholder="你的称呼" />
      <label className="field-label">出生年月日 *<input type="date" value={form.birthDate} onChange={(event) => setForm({ ...form, birthDate: event.target.value })} required /></label>
      <label className="field-label">性别 *
        <select value={form.gender} onChange={(event) => setForm({ ...form, gender: event.target.value })} required>
          <option value="">请选择</option><option value="male">男</option><option value="female">女</option><option value="other">其他</option>
        </select>
      </label>
      <div className="time-grid">
        <label>时<select value={form.birthHour} onChange={(event) => setForm({ ...form, birthHour: event.target.value })}><option value="">选填</option>{HOURS.map((item) => <option key={item} value={item}>{item}</option>)}</select></label>
        <label>分<select value={form.birthMinute} onChange={(event) => setForm({ ...form, birthMinute: event.target.value })}><option value="">选填</option>{MINUTES_SECONDS.map((item) => <option key={item} value={item}>{item}</option>)}</select></label>
        <label>秒<select value={form.birthSecond} onChange={(event) => setForm({ ...form, birthSecond: event.target.value })}><option value="">选填</option>{MINUTES_SECONDS.map((item) => <option key={item} value={item}>{item}</option>)}</select></label>
      </div>
      <TextField label="住址 *" value={form.address} onChange={(value) => setForm({ ...form, address: value })} placeholder="省市区/常住地" />
      <Button block round type="primary" nativeType="submit" className="submit-button">{submitText}</Button>
    </form>
  );
}

function TextField({ label, value, onChange, placeholder, type = 'text' }) {
  return <label className="field-label">{label}<input type={type} value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} required /></label>;
}

function SettingsPage({ deviceId, settings, updateSetting, user }) {
  return (
    <section className="panel page-panel">
      <div className="section-title"><span>设</span><h2>设置</h2></div>
      <SettingRow title="账户状态" detail={user ? `已登录：${user.phone}` : '游客模式'} />
      <SettingToggle title="消息提醒" detail="保留入口，后续可接短信/公众号提醒" checked={settings.notify} onChange={(value) => updateSetting('notify', value)} />
      <SettingToggle title="保存草稿" detail="在本机保留输入偏好" checked={settings.saveDraft} onChange={(value) => updateSetting('saveDraft', value)} />
      <SettingRow title="设备 ID" detail={deviceId.slice(0, 28)} />
      <SettingRow title="隐私说明" detail="生辰信息仅用于排盘和账户资料展示，请勿公开分享账号。" />
      <Button block round type="default" className="ghost-button" onClick={() => { localStorage.removeItem('yishu_huisuan_device_id'); Toast.success('本机设备缓存已清理，刷新后生效'); }}>清理本机缓存</Button>
    </section>
  );
}

function SettingRow({ title, detail }) {
  return <article className="setting-row"><strong>{title}</strong><small>{detail}</small></article>;
}

function SettingToggle({ title, detail, checked, onChange }) {
  return (
    <article className="setting-row setting-toggle">
      <div><strong>{title}</strong><small>{detail}</small></div>
      <button type="button" className={`switch ${checked ? 'on' : ''}`} onClick={() => onChange(!checked)}><span /></button>
    </article>
  );
}

function BottomNav({ activeTab, setActiveTab }) {
  return (
    <nav className="bottom-nav">
      {NAV_ITEMS.map((item) => <button type="button" className={activeTab === item.code ? 'active' : ''} key={item.code} onClick={() => setActiveTab(item.code)}><span>{item.mark}</span>{item.label}</button>)}
    </nav>
  );
}

function ResultView({ data }) {
  const { result, interpretation } = data;
  return (
    <section className="result-card">
      <p className="eyebrow">Hexagram Result</p>
      <h2>{result.main.name}</h2>
      <div className="gua-grid"><GuaBlock title="本卦" item={result.main} /><GuaBlock title="互卦" item={result.mutual} /><GuaBlock title="变卦" item={result.changed} /></div>
      <div className="body-use"><span>动爻：第 {result.movingLine} 爻</span><span>体用：{result.bodyUse.relation}</span></div>
      {interpretation.warning && <p className="warning">DeepSeek 暂不可用，已展示本地兜底解读：{interpretation.warning}</p>}
      <pre className="interpretation">{interpretation.content}</pre>
    </section>
  );
}

function GuaBlock({ title, item }) {
  return <div className="gua-block"><small>{title}</small><strong>{item.name}</strong><span>{item.upper.symbol} {item.lower.symbol}</span></div>;
}
